import { useCallback, useMemo } from "react"
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import ListItemText from '@mui/material/ListItemText'
import Select from '@mui/material/Select'
import Checkbox from '@mui/material/Checkbox'
import FilterListIcon from '@mui/icons-material/FilterList'
import { Box, Typography } from "@mui/material"
import { FILTER_LABELS } from '../../../constants'

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 150,
        },
    },
};

function MultiSelectFilter({ selector, modifiers: { options, selections, setSelections }, align = "center" }) {
    const handleSelect = useCallback((newValue) => {
        setSelections(state => ({ ...state, [selector]: newValue }))
    }, [selector, setSelections])

    return (
        <MultiSelectFilterImpl
            label={FILTER_LABELS[selector]}
            options={options[selector]}
            selectedSet={selections[selector]}
            handleSelect={handleSelect}
            align={align}
        />
    )
}

function MultiSelectFilterImpl({ label, options, selectedSet, handleSelect, align }) {
    const handleChange = useCallback((event) => {
        const { target: { value } } = event
        const newSet = new Set(typeof value === 'string' ? value.split(',') : value)
        handleSelect(newSet)
    }, [handleSelect])

    const selectedValues = useMemo(() => Array.from(selectedSet), [selectedSet])
    const isFiltered = useMemo(() => (
        selectedSet.size > 0 && selectedSet.size < options.length
    ), [selectedSet, options.length])

    const renderValue = useCallback((selected) => {
        const isLeftAligned = align === 'left'
        const isFiltered = selected.length > 0 && selected.length < options.length

        if (!isFiltered) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: isLeftAligned ? 'space-between' : 'center', width: '100%', gap: 0.5 }}>
                    <Typography component="span" sx={{ fontSize: 13, fontWeight: 700, textAlign: isLeftAligned ? 'left' : 'center' }}>
                        {label}
                    </Typography>
                    {isLeftAligned && (
                        <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>
                            All
                        </Typography>
                    )}
                </Box>
            )
        }

        const displayValues = selected.length <= 2
            ? selected.join(', ')
            : `${selected.length} selected`

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isLeftAligned ? 'flex-start' : 'center', justifyContent: 'center', width: '100%' }}>
                <Typography component="span" sx={{ fontSize: 10, fontWeight: 800, color: 'primary.main', textTransform: 'uppercase', lineHeight: 1 }}>
                    {label}
                </Typography>
                <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, color: 'primary.main', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: isLeftAligned ? '100%' : 80 }}>
                    {displayValues}
                </Typography>
            </Box>
        )
    }, [align, label, options.length])

    const isLeftAligned = align === 'left'

    return (
        <FormControl variant='standard' sx={{ minWidth: 80, width: '100%', textAlign: isLeftAligned ? 'left' : 'center' }}>
            <Select
                disabled={options.length === 0}
                labelId={`multi-select-${label}-label`}
                id={`multi-select-${label}`}
                multiple
                value={selectedValues}
                onChange={handleChange}
                displayEmpty
                IconComponent={FilterListIcon}
                renderValue={renderValue}
                MenuProps={MenuProps}
                disableUnderline
                sx={{
                    '& .MuiSelect-select': {
                        py: 0.75,
                        px: isLeftAligned ? 1.25 : 1,
                        pr: isLeftAligned ? 3.5 : 1,
                        fontSize: '14px',
                        display: 'flex',
                        justifyContent: isLeftAligned ? 'flex-start' : 'center',
                        alignItems: 'center',
                        minHeight: '38px',
                        bgcolor: isFiltered ? 'rgba(101, 0, 0, 0.08)' : 'transparent',
                        borderRadius: 1,
                        textAlign: isLeftAligned ? 'left' : 'center',
                        transition: 'background-color 0.2s'
                    },
                    '& .MuiSelect-icon': {
                        right: 6,
                        opacity: isFiltered ? 1 : 0.3,
                        color: isFiltered ? 'primary.main' : 'inherit',
                        fontSize: '18px',
                        transition: 'opacity 0.2s, color 0.2s'
                    },
                    '&:hover .MuiSelect-icon': {
                        opacity: 0.8
                    }
                }}
            >
                {options.map((option) => (
                    <MenuItem key={option} value={option}>
                        <Checkbox checked={selectedSet.has(option)} />
                        <ListItemText primary={option} />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    )
}

export default MultiSelectFilter
